import { supabase } from '../lib/supabase';
import { storageService } from './storageService';

/**
 * Blog Service - Handles all blog post operations
 * Converts between database snake_case and React camelCase
 */

const convertToReact = (post) => {
  if (!post) return null;
  return {
    id: post?.id,
    authorId: post?.author_id,
    title: post?.title,
    slug: post?.slug,
    content: post?.content,
    imageUrl: post?.image_url,
    isPublished: post?.is_published,
    createdAt: post?.created_at,
    updatedAt: post?.updated_at,
    publishedAt: post?.published_at,
    categoryId: post?.category_id,
    viewCount: post?.view_count,
    author: post?.user_profiles ? {
      id: post?.user_profiles?.id,
      fullName: post?.user_profiles?.full_name,
      email: post?.user_profiles?.email,
      avatarUrl: post?.user_profiles?.avatar_url
    } : null,
    category: post?.blog_categories ? {
      id: post?.blog_categories?.id,
      name: post?.blog_categories?.name,
      slug: post?.blog_categories?.slug,
      color: post?.blog_categories?.color
    } : null,
    images: post?.blog_post_images ? post?.blog_post_images?.map(img => ({
      id: img?.id,
      imageUrl: img?.image_url,
      caption: img?.caption,
      displayOrder: img?.display_order
    })) : []
  };
};

const convertToDb = (post) => {
  const dbPost = {
    title: post?.title,
    slug: post?.slug,
    content: post?.content,
    image_url: post?.imageUrl,
    is_published: post?.isPublished,
    category_id: post?.categoryId || null
  };
  
  // Only include published_at if it's provided
  if (post?.publishedAt !== undefined) {
    dbPost.published_at = post?.publishedAt;
  }
  
  return dbPost;
};

export const blogService = {
  /**
   * Get all posts (admin view - includes unpublished)
   */
  async getAllPosts() {
    const { data, error } = await supabase?.from('posts')?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email,
          avatar_url
        ),
        blog_categories (
          id,
          name,
          slug,
          color
        ),
        blog_post_images (
          id,
          image_url,
          caption,
          display_order
        )
      `)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(convertToReact) || [];
  },

  /**
   * Get published posts (public view)
   */
  async getPublishedPosts() {
    const { data, error } = await supabase?.from('posts')?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email,
          avatar_url
        ),
        blog_categories (
          id,
          name,
          slug,
          color
        )
      `)?.eq('is_published', true)?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(convertToReact) || [];
  },

  /**
   * Get single post by slug
   */
  async getPostBySlug(slug) {
    const { data, error } = await supabase?.from('posts')?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email,
          avatar_url
        ),
        blog_categories (
          id,
          name,
          slug,
          color
        ),
        blog_post_images (
          id,
          image_url,
          caption,
          display_order
        )
      `)?.eq('slug', slug)?.single();

    if (error) throw error;
    return convertToReact(data);
  },

  /**
   * Get single post by ID
   */
  async getPostById(id) {
    const { data, error } = await supabase?.from('posts')?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email,
          avatar_url
        )
      `)?.eq('id', id)?.single();

    if (error) throw error;
    return convertToReact(data);
  },

  /**
   * Create new post
   */
  async createPost(postData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const dbPost = {
      ...convertToDb(postData),
      author_id: user?.id
    };

    const { data, error } = await supabase?.from('posts')?.insert(dbPost)?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email,
          avatar_url
        ),
        blog_categories (
          id,
          name,
          slug,
          color
        )
      `)?.single();

    if (error) throw error;
    return convertToReact(data);
  },

  /**
   * Update existing post
   */
  async updatePost(id, postData) {
    const dbPost = convertToDb(postData);

    const { data, error } = await supabase?.from('posts')?.update(dbPost)?.eq('id', id)?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email,
          avatar_url
        ),
        blog_categories (
          id,
          name,
          slug,
          color
        )
      `)?.single();

    if (error) throw error;
    return convertToReact(data);
  },

  /**
   * Delete post
   */
  async deletePost(id) {
    const { error } = await supabase?.from('posts')?.delete()?.eq('id', id);

    if (error) throw error;
  },

  /**
   * Upload post cover image
   */
  async uploadCoverImage(file) {
    try {
      const url = await storageService?.uploadEventImage(file);
      return url;
    } catch (error) {
      console.error('Cover image upload failed:', error);
      throw error;
    }
  },

  /**
   * Upload multiple images for a post
   */
  async uploadPostImages(postId, files) {
    const uploadedImages = [];
    
    for (let i = 0; i < files?.length; i++) {
      const file = files?.[i];
      const imageUrl = await this.uploadCoverImage(file);
      
      const { data, error } = await supabase?.from('blog_post_images')?.insert({
        post_id: postId,
        image_url: imageUrl,
        display_order: i
      })?.select()?.single();
      
      if (error) throw error;
      uploadedImages?.push({
        id: data?.id,
        imageUrl: data?.image_url,
        displayOrder: data?.display_order
      });
    }
    
    return uploadedImages;
  },

  /**
   * Delete post image
   */
  async deletePostImage(imageId) {
    const { error } = await supabase?.from('blog_post_images')?.delete()?.eq('id', imageId);
    if (error) throw error;
  },

  /**
   * Generate URL-friendly slug from title
   */
  generateSlug(title) {
    const turkishMap = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U'
    };

    return title?.split('')?.map(char => turkishMap?.[char] || char)?.join('')?.toLowerCase()?.trim()?.replace(/[^\w\s-]/g, '')?.replace(/[\s_-]+/g, '-')?.replace(/^-+|-+$/g, '');
  },

  /**
   * Get all categories
   */
  async getAllCategories() {
    const { data, error } = await supabase?.from('blog_categories')?.select('*')?.order('name');

    if (error) throw error;
    return data?.map(cat => ({
      id: cat?.id,
      name: cat?.name,
      slug: cat?.slug,
      color: cat?.color,
      createdAt: cat?.created_at
    })) || [];
  },

  /**
   * Create new category
   */
  async createCategory(categoryData) {
    const { data, error } = await supabase?.from('blog_categories')?.insert({
      name: categoryData?.name,
      slug: this.generateSlug(categoryData?.name),
      color: categoryData?.color || '#3B82F6'
    })?.select()?.single();

    if (error) throw error;
    return {
      id: data?.id,
      name: data?.name,
      slug: data?.slug,
      color: data?.color,
      createdAt: data?.created_at
    };
  },

  /**
   * Delete category
   */
  async deleteCategory(id) {
    const { error } = await supabase?.from('blog_categories')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  /**
   * Subscribe to newsletter
   */
  async subscribeToNewsletter(email) {
    // Check if email already exists in newsletter_subscribers table
    const { data: existing, error: checkError } = await supabase
      ?.from('newsletter_subscribers')
      ?.select('email')
      ?.eq('email', email)
      ?.single();

    if (existing) {
      throw new Error('Bu e-posta adresi zaten kayıtlı');
    }

    // Insert new subscriber
    const { error } = await supabase
      ?.from('newsletter_subscribers')
      ?.insert({ email, subscribed_at: new Date()?.toISOString() });

    if (error) {
      // If table doesn't exist, just return success (graceful degradation)
      console.warn('Newsletter subscription failed:', error);
      return { success: true };
    }

    return { success: true };
  },

  /**
   * Get comments for a blog post by slug
   */
  async getCommentsByPostSlug(slug) {
    // First get the post ID from slug
    const { data: post, error: postError } = await supabase
      ?.from('posts')
      ?.select('id')
      ?.eq('slug', slug)
      ?.single();

    if (postError || !post) {
      console.error('Post not found:', postError);
      return [];
    }

    // Get approved comments for this post
    const { data, error } = await supabase?.from('blog_comments')?.select(`
        id,
        content,
        created_at,
        user_profiles (
          id,
          full_name,
          avatar_url
        )
      `)
      ?.eq('post_id', post?.id)
      ?.eq('status', 'approved')
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to fetch comments:', error);
      return [];
    }

    return data?.map(comment => ({
      id: comment?.id,
      content: comment?.content,
      createdAt: comment?.created_at,
      author: comment?.user_profiles ? {
        id: comment?.user_profiles?.id,
        fullName: comment?.user_profiles?.full_name,
        avatarUrl: comment?.user_profiles?.avatar_url
      } : null
    })) || [];
  },

  /**
   * Create a new comment on a blog post
   */
  async createComment(postId, content) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('blog_comments')?.insert({
        post_id: postId,
        user_id: user?.id,
        content: content?.trim(),
        status: 'pending',
        created_at: new Date()?.toISOString()
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get all comments (admin view - for analytics)
   */
  async getAllComments() {
    const { data, error } = await supabase?.from('blog_comments')?.select(`
        id,
        content,
        status,
        created_at,
        post_id,
        user_profiles (
          id,
          full_name,
          avatar_url
        )
      `)?.order('created_at', { ascending: false });

    if (error) throw error;
    
    return data?.map(comment => ({
      id: comment?.id,
      content: comment?.content,
      status: comment?.status,
      createdAt: comment?.created_at,
      postId: comment?.post_id,
      author: comment?.user_profiles ? {
        id: comment?.user_profiles?.id,
        fullName: comment?.user_profiles?.full_name,
        avatarUrl: comment?.user_profiles?.avatar_url
      } : null
    })) || [];
  }
};